import { baseApi } from '@/shared/api/baseApi';

import { byUrgency, dedupeAsicInvoices } from './outstanding';
import { outstandingResponseSchema, type OutstandingItem } from './outstanding.schema';

export interface OutstandingArgs {
  /** Free text over company name and document type. */
  searchBy?: string;
  /** A form number, 'invoice', or '' for everything. */
  type?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * The director's outstanding items, across every company they are on.
 *
 * Sorting and pagination are deliberately NOT query arguments. The legacy
 * screen sent `order` to the server while paginating in the browser, so sorting
 * reordered only the rows already downloaded and the page count described a
 * different dataset than the sort did. The list is small — it is one director's
 * outstanding work, not a registry — so it is fetched whole, ordered by urgency
 * here, and paginated client-side over the complete set. One source of truth.
 */
export const outstandingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOutstandingItems: build.query<OutstandingItem[], OutstandingArgs>({
      query: ({ searchBy, type, startDate, endDate }) => ({
        url: '/director/unpaid/unsigned/documents',
        params: {
          searchBy: searchBy ?? '',
          type: type === 'all' ? '' : (type ?? ''),
          startDate: startDate ?? '',
          endDate: endDate ?? '',
        },
      }),
      transformResponse: (response: unknown): OutstandingItem[] => {
        const parsed = outstandingResponseSchema.parse(response);
        const items = Array.isArray(parsed) ? parsed : parsed.data;
        return dedupeAsicInvoices(items).sort(byUrgency);
      },
      // Signing and paying both invalidate `Document` from the forms and
      // payments features, which is what refreshes this list. The legacy screen
      // hand-mutated its local array in four places instead; one of those
      // filtered on `signed || paid` where it meant `&&`, so rows disappeared
      // for the wrong reason.
      providesTags: ['Document'],
    }),
  }),
});

export const { useGetOutstandingItemsQuery } = outstandingApi;
